import { AxiosRequestConfig } from "axios";
import useAxios from "axios-hooks";
import queryString from "query-string";
import { useEffect, useMemo, useState } from "react";

import { useErrorAlert, useSuccessAlert, useUserRole } from "hooks";

import API from "constants/api";

import { BUYER } from "constants/global";
import { FetchOption } from "types/global";

function useFetchBrand<T>({
  method = "GET",
  api = API.BRAND,
  id = "",
  url = "",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
}) {
  const { setError } = useErrorAlert();

  // const { isBuyer } = useUserRole();

  const PATH = `/${BUYER}${api}`;

  const [data, setData] = useState<T>();

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !id && !url ? PATH : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data: data as T,
      isLoading,
      refetch,
      error,
    }),
    [id, url, data, isLoading, refetch],
  );
}

function useUpdateBrand<Req, Res = { id?: string }>({
  method = "POST",
  api = API.BRAND,
  id = "",
  url = "",
  successAlertEnabled = false,
  successMessage = "Submitted successfully.",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
  successAlertEnabled?: boolean;
  successMessage?: string;
}) {
  const { setError, setAppError } = useErrorAlert();
  const { setSuccess } = useSuccessAlert();

  const { isBuyer } = useUserRole();

  const PATH = isBuyer ? `/${BUYER}${api}` : api;

  const [data, setData] = useState<Res>();

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !id && !url ? PATH : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.code !== 200) {
      setAppError({
        code: fetchData?.code,
        message: fetchData?.message,
      });
    }

    if (successAlertEnabled && fetchData?.code === 200) setSuccess(successMessage);

    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data,
      isLoading,
      refetch: (payload: Req) => refetch({ data: payload }),
      error,
    }),
    [api, id, url, successAlertEnabled, successMessage, data, isLoading],
  );
}

export function useBrandOptions<T = FetchOption[]>(country = "", activeStatus = false) {
  const defaultUrl = "filter-options";

  const parsed = {
    country: "",
    status: "",
  };

  parsed.country = country ?? "";
  parsed.status = activeStatus ? "active" : "";

  const query = queryString.stringify(parsed, {
    skipEmptyString: true,
  });

  const urlWithQuery = `${defaultUrl}?${query}`;

  return useFetchBrand<T>({ url: urlWithQuery });
}
